'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { IUser } from '@/types';

import Button from '@/ui/Button';
import { Row } from '@/ui/primatives';
import * as Icon from '@/ui/Icon';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@radix-ui/react-icons';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthorization } from '@/hooks/useAuthorization';

import SqlControl from './SqlControl';
import QueryQuotaUsage from './QueryQuotaUsage';
import SqlError from './SqlControl/SqlError';

import * as Styled from './styled';
import Tag from '@/ui/Tag';
import { TDfQuery, IQueryQuotaUsage } from '@/lib/dataframeQueryApi';
import { IDataFrameField } from '@/lib/dataFrameTypes';
import ErrorBoundary from '@/components/ErrorBoundary';

const QueryContextRow: React.FC<{
  query: TDfQuery;
  queryQuotaUsage?: IQueryQuotaUsage;
  currentUser?: IUser;
  onQueryClear: (e: any) => void;
  toggleQueryVisibility: () => void;
  isOpen?: boolean;
}> = ({ query, queryQuotaUsage, currentUser, onQueryClear, toggleQueryVisibility, isOpen }) => {
  const isQueryApplied = !!query.nlp || !!query.sql;
  return (
    isQueryApplied && (
      <Styled.QueryContext>
        <Row gap={10} alignItems="center">
          <Tag variant="info" pill>
            {!!query.nlp ? 'Natural Language' : 'SQL'} Query Applied
          </Tag>
        </Row>
        {currentUser && isQueryApplied ? (
          <QueryQuotaUsage queryQuotaUsage={queryQuotaUsage} />
        ) : (
          <div>
            You must be logged in to create or modify queries <LoginSignupLinks /> to continue.
          </div>
        )}

        <Row gap={10} alignItems="center">
          <Button.Text className="danger" small onClick={onQueryClear}>
            <TrashIcon />
            <span className="hide-mobile-portrait">Remove query</span>
          </Button.Text>
          |
          <div className="toggle hide-mobile-portrait" onClick={toggleQueryVisibility}>
            <span>{isOpen ? 'Hide' : 'Show'} SQL</span>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </Row>
      </Styled.QueryContext>
    )
  );
};

const LoginSignupLinks: React.FC<{ capitalize?: boolean }> = ({ capitalize }) => {
  const pathmane = usePathname();
  const searchParams = useSearchParams();

  const redirectUrl = [pathmane, searchParams.toString()].join('?');
  const loginPath = `/login?redirect=${encodeURIComponent(redirectUrl)}`;
  const registerPath = `/register?redirect=${encodeURIComponent(redirectUrl)}`;

  return (
    <>
      <Link className="link-underline" href={loginPath}>
        {capitalize ? 'Log In' : 'log in'}
      </Link>{' '}
      or{' '}
      <Link className="link-underline" href={registerPath}>
        sign up
      </Link>
    </>
  );
};

const QueryInput: React.FC<{
  value?: string;
  disabled?: boolean;
  currentUser?: IUser;
  onRefetch?: (value: string) => void;
  onChange: (value: string) => void;
}> = ({ value: nlpString = '', disabled, currentUser, onChange, onRefetch }) => {
  const [value, setValue] = useState(nlpString);

  useEffect(() => {
    setValue(nlpString || '');
  }, [nlpString]);

  const nlpIsDirty = nlpString !== value;
  const refetchEnabled = !!onRefetch && !!value && !nlpIsDirty;

  return (
    <form action={() => onChange(value)} style={{ flex: 1 }}>
      <Styled.QueryInputWrapper>
        <Styled.QueryInput>
          <Icon.Search />
          {currentUser || value ? (
            <input
              type="text"
              name="search"
              placeholder="Query this dataset. Ask me anything!"
              value={value || ''}
              disabled={disabled || !currentUser}
              onChange={(e) => setValue(e.target.value)}
            />
          ) : (
            <div style={{ flex: 1 }}>
              <LoginSignupLinks capitalize /> to perform natural language queries.
            </div>
          )}
          <Row gap={10} alignItems="center">
            {refetchEnabled && (
              <Button.Secondary
                className="success hide-mobile-portrait"
                disabled={disabled || !currentUser}
                small
                onClick={(e) => {
                  onRefetch(value);
                  e.stopPropagation();
                }}
              >
                Refetch
              </Button.Secondary>
            )}
            <Button.Primary className="hide-mobile-portrait" disabled={!nlpIsDirty || disabled || !currentUser} small>
              Go
            </Button.Primary>
          </Row>
        </Styled.QueryInput>
      </Styled.QueryInputWrapper>
    </form>
  );
};

const QueryHeader: React.FC<{
  query?: TDfQuery;
  queryQuotaUsage?: IQueryQuotaUsage;
  isOpen?: boolean;
  disabled?: boolean;
  currentUser?: IUser;
  toggleQueryVisibility: () => void;
  onChange: (value: string | undefined) => void;
  onRefetch?: (value: string) => void;
  onQueryClear: () => void;
  children?: React.ReactNode;
}> = ({
  query,
  queryQuotaUsage,
  isOpen,
  toggleQueryVisibility,
  onChange,
  onRefetch,
  onQueryClear,
  disabled,
  currentUser,
  children,
}) => {
  const queryNlpString = query?.nlp;
  const [nlp, setNlp] = useState(queryNlpString);

  useEffect(() => {
    setNlp(queryNlpString);
  }, [queryNlpString]);

  const clearQuery = (e: any) => {
    setNlp('');
    isOpen && toggleQueryVisibility();
    e.preventDefault();
    onQueryClear();
  };

  return (
    <Row gap={10} alignItems="center">
      <Styled.QueryHeader style={{ flex: 1 }}>
        <Row gap={0} alignItems="center">
          <QueryInput
            currentUser={currentUser}
            value={nlp || queryNlpString || ''}
            onChange={onChange}
            onRefetch={onRefetch}
            disabled={disabled}
          />
          {children}
        </Row>
        {query && (
          <QueryContextRow
            query={query}
            queryQuotaUsage={queryQuotaUsage}
            toggleQueryVisibility={toggleQueryVisibility}
            onQueryClear={clearQuery}
            currentUser={currentUser}
            isOpen={isOpen}
          />
        )}
      </Styled.QueryHeader>
    </Row>
  );
};

const QueryTools: React.FC<{
  query?: TDfQuery;
  queryQuotaUsage?: IQueryQuotaUsage;
  disabled?: boolean;
  onQueryClear: () => void;
  onNlpChange: (value: string | undefined) => void;
  onRefetch?: (value: string) => void;
  onSqlChange: (value: string | undefined) => void;
  schema?: { fields: IDataFrameField[] };
  children?: React.ReactNode;
}> = ({ query, queryQuotaUsage, onQueryClear, onNlpChange, onRefetch, onSqlChange, schema, disabled, children }) => {
  const [queryVisible, setQueryVisible] = useState(false);
  const auth = useAuthorization();

  // Open after query load
  useEffect(() => {
    setQueryVisible(!!query?.sql && auth.isLoggedIn());
  }, []);

  return (
    <>
      <Styled.QuerySummary>
        <QueryHeader
          query={query}
          isOpen={queryVisible}
          onChange={onNlpChange}
          onRefetch={onRefetch}
          disabled={disabled}
          queryQuotaUsage={queryQuotaUsage}
          onQueryClear={onQueryClear}
          currentUser={auth.currentUser()}
          toggleQueryVisibility={() => setQueryVisible(!queryVisible)}
        >
          {children}
        </QueryHeader>

        {query && (
          <Styled.QueryTools className="hide-mobile-portrait" $isOpen={queryVisible}>
            <ErrorBoundary fallback={<SqlError sql={query.sql || ''} />} containerComponent="QueryTools">
              <SqlControl query={query} schema={schema} onChangeSql={onSqlChange} disabled={disabled} />
            </ErrorBoundary>
          </Styled.QueryTools>
        )}
      </Styled.QuerySummary>
    </>
  );
};

export default QueryTools;
